// 주소록 목록 페이지.
// 마이페이지 -> "배송지 관리" 진입. 기본 주소 표시, 추가/수정/삭제/기본 지정.
// 배달 가능 지역 키워드가 등록되어 있으면 안내로 노출한다.

import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useConfirm } from "@oo/ui";
import {
  deleteAddress,
  listAddresses,
  setDefaultAddress,
} from "@/features/operations/operationsApi.ts";
import { useStoreSettings } from "@/features/operations/useStoreSettings.ts";

// 배송지 목록 관리 화면.
export default function AddressesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const ops = useStoreSettings();

  const list = useQuery({ queryKey: ["addresses"], queryFn: listAddresses });

  const deleteMut = useMutation({
    mutationFn: deleteAddress,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["addresses"] }),
  });
  const defaultMut = useMutation({
    mutationFn: setDefaultAddress,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["addresses"] }),
  });

  function formatPhone(p: string): string {
    if (p.length === 11) return `${p.slice(0, 3)}-${p.slice(3, 7)}-${p.slice(7)}`;
    if (p.length === 10) return `${p.slice(0, 3)}-${p.slice(3, 6)}-${p.slice(6)}`;
    return p;
  }

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-4 px-4 py-6 pb-32">
      <header className="flex items-baseline justify-between">
        <h1 className="text-2xl font-bold">배송지 관리</h1>
        <button
          type="button"
          onClick={() => navigate("/me/addresses/new")}
          className="inline-flex min-h-touch items-center rounded bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700"
        >
          + 배송지 추가
        </button>
      </header>

      {/* 배달 가능 지역 안내 */}
      {ops.settings && ops.settings.deliveryEnabled ? (
        <DeliveryAreaNotice settings={ops.settings} />
      ) : null}

      {list.isLoading ? (
        <p className="text-sm text-ink-600">불러오는 중...</p>
      ) : list.data && list.data.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {list.data.map((a) => (
            <li key={a.id} className="rounded-lg border border-ink-200 bg-white p-4">
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-semibold">{a.label || "배송지"}</span>
                  {a.isDefault ? (
                    <span className="rounded bg-brand-600 px-2 py-0.5 text-[11px] font-semibold text-white">
                      기본
                    </span>
                  ) : null}
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => navigate(`/me/addresses/${a.id}/edit`)}
                    className="rounded px-2 py-0.5 text-ink-600 hover:bg-ink-100"
                  >
                    수정
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const ok = await confirm({
                        title: "배송지 삭제",
                        description: "이 배송지를 삭제할까요?",
                        confirmLabel: "삭제",
                      });
                      if (ok) {
                        deleteMut.mutate(a.id);
                      }
                    }}
                    disabled={deleteMut.isPending}
                    className="rounded px-2 py-0.5 text-ink-600 hover:bg-ink-100"
                  >
                    삭제
                  </button>
                </div>
              </div>
              <p className="mt-1 text-sm text-ink-900">
                {a.recipientName} ({formatPhone(a.recipientPhone)})
              </p>
              <p className="mt-1 text-sm text-ink-700">
                {a.addressLine1}
                {a.addressLine2 ? ` ${a.addressLine2}` : ""}
                {a.postalCode ? ` (${a.postalCode})` : ""}
              </p>
              {!a.isDefault ? (
                <button
                  type="button"
                  onClick={() => defaultMut.mutate(a.id)}
                  disabled={defaultMut.isPending}
                  className="mt-2 rounded-md border border-ink-300 px-3 py-1 text-xs text-ink-700 hover:bg-ink-100"
                >
                  기본 배송지로 지정
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-ink-300 p-10 text-center">
          <p className="text-base font-medium">등록된 배송지가 없습니다.</p>
          <p className="text-sm text-ink-600">배달 주문 전에 배송지를 추가해 주세요.</p>
          <button
            type="button"
            onClick={() => navigate("/me/addresses/new")}
            className="mt-2 inline-flex min-h-touch items-center rounded bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700"
          >
            + 배송지 추가
          </button>
        </div>
      )}
    </main>
  );
}

// 배달 가능 지역 안내 박스. 키워드 미설정이면 "전 지역" 안내.
function DeliveryAreaNotice({ settings }: { settings: import("@oo/shared").StoreSettingsDto }) {
  const hasKeywords = settings.deliveryAreaKeywords.length > 0;
  return (
    <section className="rounded-md border border-sky-300 bg-sky-50 p-3 text-xs text-sky-900">
      <p className="font-semibold">배달 가능 지역</p>
      {hasKeywords ? (
        <>
          <p className="mt-1">
            다음 지역으로 배달해 드립니다: {settings.deliveryAreaKeywords.join(", ")}
          </p>
          <p className="mt-1 font-semibold">
            해당 지역명이 주소에 포함되어있어야 주문이 가능합니다.
          </p>
        </>
      ) : (
        <p className="mt-1">배달 가능 지역이 별도 지정되어 있지 않습니다.</p>
      )}
      {settings.deliveryMinOrderKrw > 0 ? (
        <p className="mt-1">최소 주문 금액 {settings.deliveryMinOrderKrw.toLocaleString()}원</p>
      ) : null}
      {settings.deliveryFeeKrw > 0 ? (
        <p className="mt-1">배달비 {settings.deliveryFeeKrw.toLocaleString()}원 (현장 결제 합산)</p>
      ) : null}
    </section>
  );
}
